import { useEffect, useState } from 'react'
import { useUserLanguage } from '@/customHooks'
import { bedSizeMapper, capitalizeFirstLetter } from '@/helpers'
import { Unity } from '@/custom-types'
import { UnityStatusOptionPill } from '../atoms/UnityStatusOptionPill'

type PropertyMultiSelectionListProps = {
  selected: (unityIds: string[]) => void
  unities: Unity[]
}

const textsData = {
  en: {
    unity: {
      name: 'Name',
      status: 'Status',
      bedSize: 'Bed Size',
      privateBath: 'Bath',
      airConditioner: 'A/C',
      fan: 'Fan'
    }
  },
  es: {
    unity: {
      name: 'Nombre',
      status: 'Estado',
      bedSize: 'Cama',
      privateBath: 'Baño',
      airConditioner: 'A/C',
      fan: 'Ventilador'
    }
  }
}

export const UnityMultiSelectionList = ({ selected, unities }: PropertyMultiSelectionListProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [selectedUnityIds, setSelectedUnityIds] = useState<string[]>([])

  useEffect(() => {
    selected(selectedUnityIds)
  }, [selectedUnityIds, selected])

  const handleToggleUnity = (id: string) => {
    setSelectedUnityIds(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    )
  }

  return (
    <div className="py-6">
      <div className="h-[40vh] overflow-y-auto rounded-xl grid grid-cols-[64px_2fr_1fr_2fr_1fr_1fr_1fr] content-start w-full max-w-full min-w-[300px] min-[500px]:min-w-[500px]">
        {/* Encabezado */}
        <div className="contents">
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10"></div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.name}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.status}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.bedSize}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.privateBath}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.airConditioner}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.unity.fan}</div>
        </div>

        {/* Filas dinámicas */}
        {unities?.map((data) => (
          <div key={data.id} className="contents group">
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <input
                type="checkbox"
                checked={selectedUnityIds.includes(data.id)}
                onChange={() => handleToggleUnity(data.id)}
                className="cursor-pointer"
              />
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              {capitalizeFirstLetter(data.name)}
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <UnityStatusOptionPill option={data.currentStatusOption} />
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <p>{bedSizeMapper(data.bedSizeOption, userLanguage)}</p>
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <input type="checkbox" readOnly checked={data.privateBathroom} className="accent-primary-2" />
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <input type="checkbox" readOnly checked={data.airConditioner} className="accent-primary-2" />
            </div>
            <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
              <input type="checkbox" readOnly checked={data.fan} className="accent-primary-2" />
            </div>
          </div>
        )) ?? null}
      </div>
    </div>
  );
};
